import { FilterBar, FilterBarOpenButton, SearchBar, SearchResultsContainer } from "@kaltura/mediaspace-shared-search";
import {
    baseUrl,
    extractDefaultFilterValues,
    removeEmptyDefaultFilterValues,
    translate,
    useMediaQuery,
} from "@kaltura/mediaspace-shared-utils";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Stack, useTheme } from "@kaltura/mediaspace-shared-styled";
import {
    BaseEntryResultType,
    FilterProps,
    FilterValues,
    KmsTypeComponent,
    SupportedKmsTypeFilter,
} from "@kaltura/mediaspace-shared-types";
import { buildQuerystring } from "kaltura-typescript-client/adapters/utils";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";
import { Typography } from "@kaltura/ds-react-components";
import { ExtraHeaderBaseProps, MediaCardContext, SearchResultsSkeleton } from "@kaltura/mediaspace-shared-media-list";
import { SearchResultsData } from "@kaltura/mediaspace-shared-selectable-media-list";
import { SpanSrOnly } from "@kaltura/mediaspace-shared-ui";
import { RecycleBinHeader } from "./header/RecycleBinHeader";
import { EmptyRecycleBin } from "./EmptyRecycleBin";
import { ButtonAnalyticsProvider } from "@kaltura/mediaspace-shared-hooks";
import {
    RecycleBinContainer,
    TitleContainer,
    Title,
    ContentContainer,
    ContentFiltersContainer,
    FiltersContainer,
    SearchFormContainer,
    ResultsContainer,
} from "./styled-components";

export interface RecycleBinProps {
    filters: FilterProps<SupportedKmsTypeFilter>[];
    initialValues?: FilterValues;
    pageSize: number;
    retentionDays: number;
    bulkButtonActions?: KmsTypeComponent[];
    bulkMenuActions?: KmsTypeComponent[];
    emptyBinUrl: string;
}

const baseClassName = "kms-ds-recycle-bin";

/**
 * RecycleBin component - a container for the recycle bin
 * @page-component recycle-bin
 * @data-endpoint recyclebin-recycle-bin-ds-search-results
 * @data-endpoint recyclebin-recycle-bin-ds-explore-entry
 */
export const RecycleBin = (props: RecycleBinProps) => {
    const {
        filters,
        initialValues = {},
        pageSize,
        retentionDays,
        bulkButtonActions,
        bulkMenuActions,
        emptyBinUrl,
    } = props;

    const [filterValues, setFilterValues] = useState(() => {
        return { ...extractDefaultFilterValues(filters), ...initialValues };
    });

    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [reloadCount, setReloadCount] = useState(0);
    const handleReload = () => setReloadCount((c) => c + 1);
    const [selectedSearchResult, setSelectedSearchResult] = useState<BaseEntryResultType>();

    const theme = useTheme();
    const mdScreen = useMediaQuery(theme.breakpoints.up("md"));
    const lgDownScreen = useMediaQuery(theme.breakpoints.down("lg"));

    const isModalOpen = isFiltersModalOpen && !mdScreen;

    const filtersButtonRef = useRef<HTMLButtonElement>(null);

    const handleFilterChange = (values: FilterValues) => {
        setFilterValues(values);
        setSelectedSearchResult(undefined);
    };

    const handleKeywordSearchSubmit = (value: string) => {
        setFilterValues((filterValues) => ({
            ...filterValues,
            keyword: value,
        }));
        setSelectedSearchResult(undefined);
    };

    const minFilterValues = useMemo(
        () => removeEmptyDefaultFilterValues(filters, filterValues),
        [filters, filterValues]
    );

    const searchFilterValues = useMemo(() => ({ ...minFilterValues, reloadCount }), [minFilterValues, reloadCount]);

    // update history on filter change
    useEffect(() => {
        const updateHistory = (filterValues: FilterValues) => {
            // update query string
            const queryString = buildQuerystring(filterValues);
            window.history.pushState(null, "", `?${queryString}`);
        };

        updateHistory(minFilterValues);
    }, [minFilterValues]);

    const modalFilters = selectedSearchResult && lgDownScreen;

    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;

    let searchBarButtons;
    if (!mdScreen || modalFilters) {
        searchBarButtons = [<FilterBarOpenButton ref={filtersButtonRef} onClick={() => setIsFiltersModalOpen(true)} />];
    }

    const noMedia = () => <EmptyRecycleBin retentionDays={retentionDays} />;

    const header: KmsTypeComponent<any, ExtraHeaderBaseProps> = {
        component: RecycleBinHeader,
        name: "RecycleBinHeader",
        props: { onReload: handleReload, emptyBinUrl },
    };

    return (
        <ButtonAnalyticsProvider applicationFeature={"recycle bin"}>
            <RecycleBinContainer className={baseClassName}>
                <Stack direction={"column"} gap={3}>
                    <TitleContainer>
                        <Title variant="h2" component="h1" data-testId={"recycle-bin-title"}>
                            {translate("Recycle bin")}
                        </Title>
                        <Typography variant={"body1"} data-testId={"recycle-bin-sub-title"}>
                            {translate(
                                "Items in the recycle bin will be automatically deleted after %1 days.",
                                [retentionDays]
                            )}
                        </Typography>
                    </TitleContainer>

                    <ContentContainer
                        className={`${baseClassName}-content-container`}
                        onClose={() => setSelectedSearchResult(undefined)}
                        searchResult={selectedSearchResult}
                        filterValues={minFilterValues}
                        mediaContext={"recyclebin"}
                        callToActionComponent={<></>}
                        metaDataFetchUrl={`${baseUrl}/recyclebin/recycle-bin-ds/explore-entry/entryId/${selectedSearchResult?.object.id}`}
                    >
                        <ContentFiltersContainer inModal={modalFilters}>
                            <FiltersContainer inModal={modalFilters} className={`${baseClassName}-filters-container`}>
                                <SearchFormContainer
                                    inModal={modalFilters}
                                    className={`${baseClassName}-search-form-container`}
                                >
                                    <SearchBar
                                        buttons={searchBarButtons}
                                        keywordSearchProps={{
                                            onSubmit: handleKeywordSearchSubmit,
                                            value: filterValues["keyword"] as string,
                                            placeholder: translate("Search in Recycle Bin"),
                                            ariaLabel: translate("Search in Recycle Bin"),
                                        }}
                                    />
                                </SearchFormContainer>

                                <FilterBar
                                    filters={filters}
                                    values={filterValues}
                                    onChange={handleFilterChange}
                                    isModalOpen={isModalOpen}
                                    inModal={modalFilters}
                                    onModalClose={() => {
                                        setIsFiltersModalOpen(false);
                                        filtersButtonRef.current?.focus();
                                    }}
                                    resultsContainerId="search-results"
                                />
                            </FiltersContainer>
                            <MediaCardContext.Provider value={{ openLinksInNewTab: !isKaf, disableLinks: true }}>
                                <ResultsContainer
                                    id="search-results"
                                    tabIndex={-1}
                                    role="region"
                                    aria-labelledby="search-results-label"
                                    className={`${baseClassName}-results-container`}
                                >
                                    <SpanSrOnly id="search-results-label">{translate("Search results")}</SpanSrOnly>
                                    <SearchResultsContainer
                                        searchUrl={`${baseUrl}/recyclebin/recycle-bin-ds/search-results`}
                                        isWideLayout={!selectedSearchResult}
                                        filterValues={searchFilterValues}
                                        pageSize={pageSize}
                                        onEntryShowMore={(item) => setSelectedSearchResult(item)}
                                        searchResultsDisplayComponent={SearchResultsData}
                                        searchResultsSkeletonComponent={SearchResultsSkeleton}
                                        bulkButtonActions={bulkButtonActions}
                                        bulkMenuActions={bulkMenuActions}
                                        displayComponentProps={{
                                            noMediaComponent: noMedia,
                                            header,
                                            additionalInvalidation: () => setSelectedSearchResult(undefined),
                                        }}
                                    />
                                </ResultsContainer>
                            </MediaCardContext.Provider>
                        </ContentFiltersContainer>
                    </ContentContainer>
                </Stack>
            </RecycleBinContainer>
        </ButtonAnalyticsProvider>
    );
};

export default RecycleBin;
